import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import { Offcanvas } from 'react-bootstrap';
import { useAuth } from '../Context/auth';
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import Pagination from '../Components/Pagination/Pagination';
import './Estoque.css';

// --- COMPONENTE DO MODAL (Mantido) ---
const VisualizarGradeModal = ({ isVisible, onClose, tamanhosData = [], itemNome, cor, gradeNome }) => {
    const safeTamanhosData = Array.isArray(tamanhosData) ? tamanhosData : [];
    const total = safeTamanhosData.reduce((sum, item) => sum + (Number(item.quantidade) || 0), 0);

    if (!isVisible) return null;

    const modalStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
        justifyContent: 'center', alignItems: 'center', zIndex: 1060
    };
    const contentStyle = {
        backgroundColor: 'white', padding: '20px', borderRadius: '8px',
        width: '90%', maxWidth: '500px', boxShadow: '0 5px 15px rgba(0,0,0,0.5)'
    };

    return (
        <div style={modalStyle}>
            <div style={contentStyle}>
                <div className="modal-header d-flex justify-content-between align-items-center p-0 pb-2 border-bottom">
                    <div>
                        <h5 className="modal-title mb-0">{gradeNome || 'Estoque por Tamanho'}</h5>
                        <small className="text-muted">{itemNome} {cor && `- ${cor}`}</small>
                    </div>
                    <button type="button" className="btn-close" onClick={onClose}></button>
                </div>
                <div className="modal-body p-0 py-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <div className="d-flex justify-content-between mb-3 fw-bold border-bottom pb-1">
                        <span>Tamanho</span>
                        <span>Quantidade</span>
                    </div>
                    {safeTamanhosData.map((item, index) => (
                        <div key={item.tamanho || index} className="d-flex justify-content-between align-items-center mb-2 p-2 border-bottom">
                            <span className="fw-bold">{item.tamanho}</span>
                            <span>{item.quantidade}</span>
                        </div>
                    ))}
                    {safeTamanhosData.length === 0 && <p className='text-muted text-center'>Nenhum tamanho detalhado encontrado.</p>}
                </div>
                <div className="modal-footer p-0 pt-3 border-top d-flex justify-content-end">
                    <span className="me-auto fw-bold">TOTAL: {total}</span>
                    <button type="button" className="btn btn-secondary" onClick={onClose}>Fechar</button>
                </div>
            </div>
        </div>
    );
};


// --- COMPONENTE PRINCIPAL ---
function Estoque() {
    const { currentUser } = useAuth();
    const [estoque, setEstoque] = useState([]);
    const [loading, setLoading] = useState(true);

    // Listas para Selects do Filtro
    const [listaMarcas, setListaMarcas] = useState([]);
    const [listaGrades, setListaGrades] = useState([]);

    // Filtros
    const [showFiltros, setShowFiltros] = useState(false);
    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');

    const [filtrosForm, setFiltrosForm] = useState({
        tipo: 'todos', // todos, Produto, Insumo
        exibirZerado: false,
        codigo: '',
        descricao: '',
        marca: '',
        fornecedor: '', // Nome do fornecedor
        cor: '',
        grade_id: ''
    });

    const [filtrosAplicados, setFiltrosAplicados] = useState({
        tipo: 'todos',
        exibirZerado: false,
        codigo: '',
        descricao: '',
        marca: '',
        fornecedor: '',
        cor: '',
        grade_id: ''
    });

    // Modal
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalDataItem, setModalDataItem] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Hook para chamadas API
    // Hook para chamadas API
    const apiCall = useCallback(async (url) => {
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const response = await fetch(`${API_URL}${url}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Falha ao buscar ${url}`);
        }
        if (response.status === 204) return null;
        return response.json();
    }, [currentUser]);

    // Busca dados do estoque
    useEffect(() => {
        const buscarDados = async () => {
            if (!currentUser) { setLoading(false); return; }
            try {
                setLoading(true);
                // Sempre busca tudo, a filtragem é no frontend sobre o resultado agrupado
                // Mas podemos passar 'showZeroStock' pro backend pra ele trazer os zerados se necessário
                // Como o backend já foi ajustado pra aceitar showZeroStock=true, enviamos se o filtro aplicado mandar.

                const params = new URLSearchParams();
                if (textoPesquisa) {
                    params.append('search', textoPesquisa);
                }
                // ATENÇÃO: Se o backend filtra 'showZeroStock' apenas se for 'true', e exclui se for false/undefined.
                // Precisamos garantir que o backend traga tudo que precisamos.
                // Se o usuário quer "Exibir Zerado", mandamos true.
                // Se o usuário NÃO quer, mandamos false (ou nada), e o backend filtra > 0.
                if (filtrosAplicados.exibirZerado) {
                    params.append('showZeroStock', 'true');
                }

                const data = await apiCall(`/api/estoque?${params.toString()}`);
                setEstoque(data || []);
            } catch (error) {
                console.error("Erro ao buscar estoque:", error);
                Swal.fire('Erro', 'Não foi possível carregar os dados de estoque.', 'error');
                setEstoque([]);
            } finally {
                setLoading(false);
            }
        };
        buscarDados();
    }, [textoPesquisa, filtrosAplicados.exibirZerado, apiCall, currentUser]);

    // Busca dados auxiliares para os filtros (Marcas, Fornecedores, Grades)
    // Busca dados auxiliares para os filtros (Marcas, Fornecedores, Grades)
    // Busca dados auxiliares para os filtros (Marcas, Fornecedores, Grades)
    useEffect(() => {
        const carregarListas = async () => {
            if (!currentUser) return;
            try {
                // Executa em paralelo
                const [marcas, grades] = await Promise.all([
                    apiCall('/api/marcas').catch(() => []),
                    apiCall('/api/grades').catch(() => [])
                ]);
                setListaMarcas(marcas || []);
                setListaGrades(grades || []);
            } catch (err) {
                console.error("Erro ao carregar listas de filtros:", err);
            }
        };

        if (showFiltros && listaMarcas.length === 0) { // Carrega apenas na primeira vez que abre
            carregarListas();
        }
    }, [showFiltros, apiCall, currentUser, listaMarcas.length]);



    // Auto-search Debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            setTextoPesquisa(pesquisa);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(timer);
    }, [pesquisa]);


    const handleClearSearch = () => {
        setPesquisa('');
        setTextoPesquisa('');
        // Reseta também o filtro de texto dentro do avançado? Não, são coisas separadas geralmente.
        // Mas se a 'barra de busca' é atalho para 'descricao/codigo', deveríamos decidir.
        // Por padrão, a barra superior é um filtro rápido universal. O sidebar é granular.
        // Mantenho separado.
        setCurrentPage(1);
    };

    // --- Filtros & Agrupamento ---
    const handleFiltroChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;
        setFiltrosForm(prev => ({ ...prev, [name]: val }));
    };

    const handleKeyDownFilters = (e) => {
        if (e.key === 'Enter') {
            handleAplicarFiltros();
        }
    };

    const handleAplicarFiltros = () => {
        setFiltrosAplicados(filtrosForm);
        setShowFiltros(false);
        setCurrentPage(1);
    };

    const handleLimparFiltros = () => {
        const limpo = {
            tipo: 'todos',
            exibirZerado: false,
            codigo: '',
            descricao: '',
            marca: '',
            fornecedor: '',
            cor: '',
            grade_id: ''
        };
        setFiltrosForm(limpo);
        setFiltrosAplicados(limpo);
        setPesquisa('');
        setTextoPesquisa('');
        setCurrentPage(1);
    };


    const estoqueAgrupado = useMemo(() => {
        // 1. Filtragem Inicial (Item a Item)
        const filteredItems = estoque.filter(item => {
            const f = filtrosAplicados;
            const itemLow = (val) => (val || '').toLowerCase();
            const filterLow = (val) => (val || '').toLowerCase();

            // Tipo (Lógica Corrigida: verifica preenchimento dos IDs)
            if (f.tipo === 'Produto' && !item.produto_id) return false;
            if (f.tipo === 'Insumo' && !item.insumo_id) return false;

            // Código
            if (f.codigo && !itemLow(item.item_codigo).includes(filterLow(f.codigo))) return false;

            // Descrição
            if (f.descricao && !itemLow(item.item_descricao).includes(filterLow(f.descricao))) return false;

            // Marca (Texto - busca na descrição da marca)
            if (f.marca && !itemLow(item.marca_nome).includes(filterLow(f.marca))) return false;

            // Fornecedor (Texto - busca Nome ou Fantasia)
            if (f.fornecedor) {
                const termo = filterLow(f.fornecedor);
                const matchNome = itemLow(item.fornecedor_nome).includes(termo);
                const matchFantasia = itemLow(item.fornecedor_fantasia).includes(termo);
                if (!matchNome && !matchFantasia) return false;
            }

            // Cor
            if (f.cor && !itemLow(item.cor).includes(filterLow(f.cor))) return false;

            // Grade
            if (f.grade_id && String(item.grade_id) !== String(f.grade_id)) return false;

            return true;
        });

        // 2. Agrupamento
        const grouped = filteredItems.reduce((acc, item) => {
            const itemId = item.produto_id || item.insumo_id;
            const key = `${item.tipo_item}-${itemId}-${item.cor || 'sem_cor'}-${item.grade_id || 'sem_grade'}`;
            if (!acc[key]) {
                acc[key] = {
                    key, produto_id: item.produto_id, insumo_id: item.insumo_id,
                    item_codigo: item.item_codigo, item_descricao: item.item_descricao,
                    tipo_item: item.tipo_item, cor: item.cor, grade_id: item.grade_id,
                    grade_descricao: item.grade_descricao, quantidadeTotal: 0, tamanhos: [],

                    // Dados extras
                    marca_nome: item.marca_nome,
                    fornecedor_nome: item.fornecedor_nome,
                    fornecedor_fantasia: item.fornecedor_fantasia
                };
            }
            acc[key].quantidadeTotal += Number(item.quantidade_atual || 0);
            if (item.tipo_item === 'Produto' && item.tamanho && item.grade_id) {
                if (!acc[key].tamanhos.some(t => t.tamanho === item.tamanho)) {
                    acc[key].tamanhos.push({ tamanho: item.tamanho, quantidade: Number(item.quantidade_atual || 0) });
                    acc[key].tamanhos.sort((a, b) => String(a.tamanho).localeCompare(String(b.tamanho), undefined, { numeric: true, sensitivity: 'base' }));
                }
            }
            return acc;
        }, {});

        // 3. Filtragem Pós-Agrupamento (Exibir Zerado)
        let resultList = Object.values(grouped);

        if (!filtrosAplicados.exibirZerado) {
            resultList = resultList.filter(g => g.quantidadeTotal > 0);
        }

        return resultList;

    }, [estoque, filtrosAplicados]);


    // Handler para abrir o modal de detalhes
    const handleVisualizarGrade = (itemAgrupado) => {
        if (itemAgrupado.tipo_item === 'Produto' && itemAgrupado.tamanhos && itemAgrupado.tamanhos.length > 0) {
            setModalDataItem(itemAgrupado);
            setIsModalVisible(true);
        } else {
            console.log("Visualização de grade não aplicável para este item.");
        }
    };

    // Pagination Logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentEstoque = estoqueAgrupado.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    const changeItemsPerPage = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    if (loading && !currentUser) { return null; }

    const hasActiveFilters = (
        filtrosAplicados.tipo !== 'todos' ||
        filtrosAplicados.exibirZerado ||
        filtrosAplicados.codigo ||
        filtrosAplicados.descricao ||
        filtrosAplicados.marca ||
        filtrosAplicados.fornecedor ||
        filtrosAplicados.cor ||
        filtrosAplicados.grade_id
    );

    return (
        <div className="estoque-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                {/* Modal */}
                {modalDataItem && (<VisualizarGradeModal isVisible={isModalVisible} onClose={() => { setIsModalVisible(false); setModalDataItem(null); }} tamanhosData={modalDataItem.tamanhos} itemNome={modalDataItem.item_descricao} cor={modalDataItem.cor} gradeNome={modalDataItem.grade_descricao} />)}

                <div className="estoque-header">
                    <h1 className="estoque-title">
                        <i className="fas fa-boxes text-primary"></i>
                        Análise de <span className="highlight">Estoque</span>
                    </h1>
                </div>

                <main className="estoque-content">
                    <div className="estoque-controls">
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por código, descrição, código de barras..."
                                value={pesquisa}
                                onChange={e => setPesquisa(e.target.value)}
                            />
                        </div>


                        <button
                            className={`btn-icon-custom ${hasActiveFilters ? 'btn-icon-active' : ''}`}
                            onClick={() => setShowFiltros(true)}
                            title="Filtros Avançados"
                            style={{ position: 'relative' }}
                        >
                            <i className="fas fa-filter"></i>
                            {hasActiveFilters && (
                                <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle" style={{ width: '10px', height: '10px' }}>
                                    <span className="visually-hidden">Filtros ativos</span>
                                </span>
                            )}
                        </button>

                        <button className="btn-icon-custom" onClick={() => { handleClearSearch(); handleLimparFiltros(); }} title="Limpar tudo">
                            <i className="fas fa-sync-alt"></i>
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                            <p className="mt-2 text-muted">Carregando estoque...</p>
                        </div>
                    ) : (
                        <>
                            <div className="estoque-grid-container">
                                <table className="table-custom">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '10%' }}>Tipo</th>
                                            <th style={{ width: '25%' }}>Item</th>
                                            <th style={{ width: '10%' }}>Cor</th>
                                            <th style={{ width: '10%' }}>Grade</th>
                                            <th style={{ width: '10%' }}>Qtd.</th>
                                            <th style={{ width: '5%', textAlign: 'center' }}>Tam.</th>
                                            <th style={{ width: '15%' }}>Fornecedor</th>
                                            <th style={{ width: '15%' }}>Marca</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentEstoque.map(item => (
                                            <tr key={item.key}>
                                                <td data-label="Tipo">
                                                    <span className={`status-badge ${item.tipo_item === 'Produto' ? 'status-produto' : 'status-insumo'}`}>
                                                        {item.tipo_item}
                                                    </span>
                                                </td>
                                                <td data-label="Item">
                                                    <span className="fw-bold">{item.item_codigo}</span> <br />
                                                    <span className="text-muted small">{item.item_descricao}</span>
                                                </td>
                                                <td data-label="Cor">{item.cor || '—'}</td>
                                                <td data-label="Grade">{item.tipo_item === 'Produto' ? (item.grade_descricao || '—') : 'N/A'}</td>
                                                <td className="fw-bold fs-6" data-label="Qtd.">{item.quantidadeTotal}</td>
                                                <td className="text-center" data-label="Tam.">
                                                    {item.tipo_item === 'Produto' && item.tamanhos && item.tamanhos.length > 0 ? (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary border-0"
                                                            title="Visualizar estoque por tamanho"
                                                            onClick={() => handleVisualizarGrade(item)}
                                                        >
                                                            <i className="fas fa-th"></i>
                                                        </button>
                                                    ) : (
                                                        <span className="text-muted">—</span>
                                                    )}
                                                </td>
                                                <td data-label="Fornecedor">
                                                    {item.fornecedor_nome ? (
                                                        <div className="small text-truncate" title={item.fornecedor_nome}>
                                                            {item.fornecedor_nome}
                                                            {item.fornecedor_fantasia && item.fornecedor_fantasia !== item.fornecedor_nome && (
                                                                <span className="text-muted d-block" style={{ fontSize: '0.8em' }}>({item.fornecedor_fantasia})</span>
                                                            )}
                                                        </div>
                                                    ) : <span className="text-muted">—</span>}
                                                </td>
                                                <td data-label="Marca">
                                                    {item.marca_nome ? (
                                                        <div className="small text-truncate" title={item.marca_nome}>{item.marca_nome}</div>
                                                    ) : <span className="text-muted">—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        {estoqueAgrupado.length === 0 && (
                                            <tr>
                                                <td colSpan="8" className="text-center text-muted py-5">
                                                    <i className="fas fa-box-open fa-3x mb-3 d-block opacity-25"></i>
                                                    Nenhum item encontrado no estoque com os filtros atuais.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                totalItems={estoqueAgrupado.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={paginate}
                                onItemsPerPageChange={changeItemsPerPage}
                            />
                        </>
                    )}
                </main>
            </div>

            {/* --- SIDEBAR DE FILTROS AVANÇADOS --- */}
            <Offcanvas show={showFiltros} onHide={() => setShowFiltros(false)} placement="end" style={{ zIndex: 10500 }}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title><i className="fas fa-filter me-2"></i>Filtros de Estoque</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    <div className="d-grid gap-3" onKeyDown={handleKeyDownFilters}>

                        <div className="d-grid gap-2">
                            <button className="btn btn-primary-custom" onClick={handleAplicarFiltros}>
                                <i className="fas fa-check me-2"></i>Aplicar Filtros
                            </button>
                            <button className="btn btn-outline-secondary" onClick={handleLimparFiltros}>
                                <i className="fas fa-eraser me-2"></i>Limpar Filtros
                            </button>
                        </div>

                        <hr className="m-0" />

                        {/* Exibir Zerado */}
                        <div className="form-check form-switch bg-light p-3 rounded">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="checkZeradoFilter"
                                name="exibirZerado"
                                checked={filtrosForm.exibirZerado}
                                onChange={handleFiltroChange}
                            />
                            <label className="form-check-label fw-bold" htmlFor="checkZeradoFilter">Exibir Estoque Zerado</label>
                        </div>

                        {/* Tipo */}
                        <div>
                            <label className="form-label small fw-bold">Tipo de Item</label>
                            <select className="form-select" name="tipo" value={filtrosForm.tipo} onChange={handleFiltroChange}>
                                <option value="todos">Todos</option>
                                <option value="Produto">Produtos</option>
                                <option value="Insumo">Insumos</option>
                            </select>
                        </div>

                        {/* Código */}
                        <div>
                            <label className="form-label small fw-bold">Código</label>
                            <input type="text" className="form-control" name="codigo" value={filtrosForm.codigo} onChange={handleFiltroChange} placeholder="Ex: 001" />
                        </div>

                        {/* Descrição */}
                        <div>
                            <label className="form-label small fw-bold">Descrição</label>
                            <input type="text" className="form-control" name="descricao" value={filtrosForm.descricao} onChange={handleFiltroChange} placeholder="Nome do item..." />
                        </div>

                        {/* Marca */}
                        <div>
                            <label className="form-label small fw-bold">Marca</label>
                            <input type="text" className="form-control" name="marca" value={filtrosForm.marca} onChange={handleFiltroChange} placeholder="Ex: Nike" />
                        </div>

                        {/* Fornecedor */}
                        <div>
                            <label className="form-label small fw-bold">Fornecedor</label>
                            <input type="text" className="form-control" name="fornecedor" value={filtrosForm.fornecedor} onChange={handleFiltroChange} placeholder="Nome ou Fantasia" />
                        </div>

                        {/* Cor */}
                        <div>
                            <label className="form-label small fw-bold">Cor</label>
                            <input type="text" className="form-control" name="cor" value={filtrosForm.cor} onChange={handleFiltroChange} placeholder="Ex: Azul" />
                        </div>

                        {/* Grade */}
                        <div>
                            <label className="form-label small fw-bold">Grade</label>
                            <select className="form-select" name="grade_id" value={filtrosForm.grade_id} onChange={handleFiltroChange}>
                                <option value="">Todas</option>
                                {listaGrades.map(g => (
                                    <option key={g.id} value={g.id}>{g.descricao}</option>
                                ))}
                            </select>
                        </div>

                    </div>
                </Offcanvas.Body>
            </Offcanvas>

        </div>
    );
}

export default Estoque;